<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.file-invoice-dollar-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('View sale order') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <button @click="deleteSaleModal = true" class="btn btn-red rounded-none rounded-l-md" type="button">
            {{ $t('Delete') }}
          </button>
          <router-link class="btn btn-app rounded-none" :to="`/print/sale-order/${sale.uuid}`">
            {{ $t('Print') }}
          </router-link>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin/sale-orders">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-10 gap-2">
        <loading :status="loading"></loading>
        <div class="col-span-10 md:col-span-7 bg-white shadow border-t-2 border-green-600 overflow-hidden rounded-md p-2 sm:p-6">
          <div class="flex items-center">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-100 text-gray-900 rounded-md">
                  <th class="capitalize tracking-wider">{{ $t('No') }}</th>
                  <th class="w-3/5 capitalize tracking-wider text-left pl-2 py-3">{{ $t('Item') }}</th>
                  <th class="capitalize tracking-wider">{{ $t('Qty') }}</th>
                  <th class="capitalize tracking-wider">{{ $t('Price') }}</th>
                  <th class="capitalize tracking-wider">{{ $t('SubTotal') }}</th>
                </tr>
              </thead>
              <tbody class="divide divide-gray-200">
                <tr class="text-gray-900 text-center py-2" v-for="(item, index) in sale.items" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td class="text-left pl-2 py-2">
                    {{ item.name }}
                    <small v-if="item.title">({{ item.title }})</small>
                    <br />
                    <small v-if="item.serial_number">({{ item.serial_number }})</small>
                    <small v-if="item.identity">({{ item.identity }})</small>
                  </td>
                  <td>{{ item.qty }}</td>
                  <td>{{ item.price | currency }}</td>
                  <td>{{ item.sub_total | currency }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="w-full mb-1 mt-1 py-2 overflow-hidden">
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Items') }} :</p>
              <p class="pl-2">{{ sale.cart_total_items }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Subtotal') }} :</p>
              <p class="pl-2">{{ sale.sub_total | currency }}</p>
            </div>
          </div>
          <div class="w-full mb-1 mt-1 py-2 overflow-hidden">
            <div class="px-1 flex justify-between font-semibold text-black">
              <template v-if="sale.tax.is_tax_fix">
                <p>{{ $t('Tax') }} ({{ sale.tax.rate }} {{ $t('fix') }})</p>
              </template>
              <template v-else>
                <p>{{ $t('Tax') }} ({{ sale.tax.rate }}{{ sale.tax.is_tax_fix ? '' : '%' }}) (+)</p>
              </template>
              <p class="pl-2">{{ sale.tax_amount | currency }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Shipping') }} (+)</p>
              <p class="pl-2">{{ sale.shipping | currency }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Discount') }} (-)</p>
              <p class="pl-2">{{ sale.discount | currency }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Total payable') }}</p>
              <p class="pl-2">{{ sale.payable | currency }}</p>
            </div>
          </div>
          <div class="overflow-hidden">
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Recipient amount') }}</p>
              <p class="pl-2">{{ sale.recepient_amount | currency }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Change') }} :</p>
              <p class="pl-2">{{ sale.change_amount | currency }}</p>
            </div>
          </div>
          <div class="mt-1 overflow-hidden">
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Expected estimated profit') }}</p>
              <p class="pl-2">{{ sale.expected_profit | currency }}</p>
            </div>
            <div class="px-1 flex justify-between font-semibold text-black">
              <p>{{ $t('Gain profit after all') }}</p>
              <p class="pl-2">{{ sale.profit_after_all | currency }}</p>
            </div>
            <div class="bg-gray-100 py-2 mt-1 overflow-hidden rounded-md">
              <div class="px-4">
                <strong>{{ $t('Note') }}</strong>
                <p class="pl-2 text-gray-600">{{ sale.note }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-10 md:col-span-3 bg-white shadow border-t-2 border-green-600 overflow-hidden rounded-md p-2 sm:p-6">
          <div class="flex place-content-center">
            <barcode width="2" :value="sale.tracking" class="self-center" height="25" />
          </div>
          <div class="my-2">
            <label class="block font-bold uppercase">{{ $t('Sale Invoice date') }}</label>
            <div>{{ sale.created_at }}</div>
          </div>
          <div class="my-2">
            <label class="block font-bold uppercase">{{ $t('Sale Invoice ID') }}</label>
            {{ sale.tracking }} [ {{ sale.id }} ]
          </div>

          <div class="my-4" v-if="sale.tax.tax_id">
            <label class="block font-bold uppercase">{{ $t('Tax Identity') }} ({{ $t('VAT') }})</label>
            {{ sale.tax.tax_id }}
          </div>
          <div class="my-2">
            <label class="block font-bold uppercase">{{ $t('Tax rate') }}</label>
            {{ sale.tax.rate }}
            {{ sale.tax.tax_rate }}
          </div>
          <div class="my-2">
            <label class="block font-bold uppercase">{{ $t('Tax fixed') }}</label>
            {{ sale.tax.is_tax_fix }}
          </div>
          <div class="my-2">
            <label class="block font-bold uppercase">{{ $t('Tax Included') }}</label>
            {{ sale.tax.is_tax_included }}
          </div>
        </div>
      </div>
    </div>
    <sale-delete-model :show="deleteSaleModal" @cancelled="deleteSaleModal = false" @confirmed="deleteSale">
      <template #title>{{ $t('Delete sale order') }}</template>
      {{ $t('Are you sure you want to delete the sale order ?') }}
    </sale-delete-model>
  </main>
</template>
<script>
import VueBarcode from 'vue-barcode';
import confirmationModel from '@/components/layout/admin/confirmation';

export default {
  metaInfo() {
    return {
      title: this.$i18n.t('Sale order'),
    };
  },
  components: {
    barcode: VueBarcode,
    SaleDeleteModel: confirmationModel,
  },
  data() {
    return {
      loading: true,
      deleteSaleModal: false,
      sale: {
        qr_link: null,
        discount: 0,
        shipping: 0,
        tax: {
          rate: 0,
          tax_id: null,
          is_tax_included: false,
          is_tax_fix: false,
        },
      },
    };
  },
  mounted() {
    this.getSaleOrder();
  },
  methods: {
    deleteSale() {
      axios
        .delete(route('sale-orders.destroy', this.$route.params.uuid))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/sale-orders');
        })
        .catch(() => {
          this.deleteSaleModal = false;
        });
    },
    getSaleOrder() {
      this.loading = true;
      axios
        .get(route('sale-orders.show', this.$route.params.uuid))
        .then((response) => {
          this.sale = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
